import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Megaphone, Mail, Search, BarChart3, Users, Image, Send, Target, Zap, RefreshCcw, FileBarChart, Sparkles, CheckCircle2, Lock } from "lucide-react";
import { SectionLabel, HighlightText, ToolPill, SectionHeading } from "@/components/Bits";
import ComparisonTable from "@/components/ComparisonTable";
import IndustriesGrid from "@/components/IndustriesGrid";
import TeamCarousel from "@/components/TeamCarousel";
import FAQAccordion from "@/components/FAQAccordion";
import FinalCTA from "@/components/FinalCTA";
import { INDUSTRIES_MARKETING} from "@/lib/content";



const PROBLEMS = [
  { title: "The Inconsistency Problem", desc: "Most businesses publish content in bursts, then go quiet for weeks. Your audience notices. Consistent execution is what separates brands that grow from brands that plateau." },
  { title: "The Oversight Problem", dark: true, desc: "A freelancer working independently has no one checking their work. With Aidify, every Marketing VA is also overseen by a US partner reviewing results, So your ad spend isn't burning out unnoticed." },
  { title: "The Capacity Problem", sage: true, desc: "Good marketing needs someone watching it every day, not someone you ping when you're stuck. Our Marketing VAs are full-time, dedicated to your account and working structured hours inside a managed office." },
  { title: "The Tool Problem", desc: "Marketing today runs on a dozen platforms and an expanding set of AI tools. Most freelancers are strong on one or two. Our Marketing VAs are trained across the full stack before they ever touch your account." },
];

const MODEL = [
  { icon: Lock, title: "Full-Time. Office-Based. Supervised.", quote: "79% of remote work failures trace back to accountability gaps. We close that gap before it opens.", desc: "Your Marketing VA isn't juggling five other clients from a coffee shop. They work full-time, in our managed office, on your account. Every specialist works structured hours, inside a professional environment, with a manager who reviews their output daily." },
  { icon: Users, title: "AI-Trained. Not AI-Dependent.", dark: true, quote: "AI produces volume. Our Marketing VAs produce judgement. The combination is what gets your campaigns out faster and your ad spend working harder.", desc: "Our Marketing VAs are certified in the AI tools that make modern marketing faster and sharper, ChatGPT, Jasper, Midjourney and AI analytics tools daily. Not to replace their thinking, but to make it go further. They also know when not to use it, so your brand keeps sounding like itself." },
  { icon: Lock, title: "A US Partner Who Stays in the Loop", quote: "Every Aidify client has a named US-based partner. Not a support ticket. A person.", desc: "Your US partner knows your industry. They review the work. They flag issues before you see them. And when an AI tool produces something that needs a human call and on brand voice, strategy or audience fit, that partner makes it. No chasing. No wondering. No surprises." },
  { icon: Users, title: "Process-Driven From Day One", dark: true, quote: "AI tools sit inside the process and not instead of it.", desc: "We don't hand you a Marketing VA and say good luck. Before work begins, we map your processes, document your brand standards and build the workflow your specialist follows every single day. Your brand runs the same way on day one as it does on day one hundred." },
];

const AI_FOUR_WAYS = [
  { title: "Content at Scale", desc: "Our Marketing VAs use ChatGPT, Jasper and Copy.ai to produce first drafts, repurpose content across formats and keep your publishing calendar full. Every piece is reviewed, edited and approved by a human before it reaches your audience. Speed without sloppiness." },
  { title: "Campaign Analysis", desc: "AI analytics, Google Analytics 4, Triple Whale and Northbeam surface patterns in your data in minutes that would take a human analyst hours. Our Marketing VAs are trained to read those patterns and act on them, not just forward you a report." },
  { title: "Ad Creative Testing", desc: "Our paid Marketing VAs use AI-powered creative tools to generate and test multiple ad variations faster than any traditional process allows. Winning angles get identified sooner. Underperforming ads get cut before they drain your budget." },
  { title: "Smarter Monitoring. Always.", desc: "Hootsuite, Sprout and Klaviyo AI monitor your campaigns, social channels and email performance continuously. Our Marketing VAs act on those signals, adjusting send times, rotating creatives and flagging anomalies, so nothing slips for a week unnoticed." },
];

// const MODEL = [
//   { title: "Full-Time. Office-Based. Supervised.", quote: "79% of remote work failures trace back to accountability gaps. We close that gap before it opens.", desc: "Your Marketing VA isn't juggling five other clients from a coffee shop. They work full-time, in our managed office, on your account every specialist with a manager reviewing their output daily." },
//   { title: "AI-Trained. Not AI-Dependent.", dark: true, quote: "AI produces volume. Our Marketing VAs produce judgement. The combination is what gets your campaigns out faster and your ad spend working harder.", desc: "Certified in ChatGPT, Jasper, Midjourney and AI analytics tools. Not to replace their thinking, but to make it go further. They also know when not to use it, so your brand keeps sounding like itself." },
//   { title: "A US Partner Who Stays in the Loop", quote: "Every Aidify client has a named US-based partner. Not a support ticket. A person.", desc: "Your US partner knows your industry. They review the work, flag issues before you see them and make brand-voice calls when AI output needs a human touch." },
//   { title: "Process-Driven From Day One", dark: true, quote: "AI tools sit inside the process — not instead of it.", desc: "Before work begins, we map your processes, document your brand standards and build the workflow your specialist follows every single day." },
// ];


const AI_MARKETING_TOOLS = [
  { name: "ChatGPT", desc: "Content drafting, copy variants, email subject lines, creative briefing" },
  { name: "Jasper", desc: "Long-form content, blog posts and ad copy at scale" },
  { name: "Copy.ai", desc: "Short-form copy, social captions, product descriptions" },
  { name: "Midjourney", desc: "Ad creative concepts, social visuals, campaign imagery" },
  { name: "Grammarly", desc: "Copy editing, tone checking, brand voice consistency" },
  { name: "Notion AI", desc: "Workflow documentation, content planning, SOPs" },
  { name: "Surfer SEO", desc: "AI-assisted content optimisation and keyword integration" },
  { name: "Perplexity", desc: "Research and trend identification for content strategy" },
  { name: "Triple Whale", desc: "AI-powered ecommerce analytics and attribution" },
  { name: "Google Gemini", desc: "Google Ads and Analytics integration, campaign insight" },
];

const WHAT_THEY_HANDLE = [
  { icon: Users, title: "Social Media Management", desc: "Content planned, designed, scheduled and posted across your platforms." },
  { icon: Mail, title: "Email Marketing", desc: "Campaigns planned, written, designed and sent. Subject lines optimized by behavior data." },
  { icon: Search, title: "SEO Management", desc: "Keyword research, on-page fixes and a content strategy built on data and not on guesswork." },
  { icon: Image, title: "Content & Copywriting", desc: "Blog posts, captions, newsletters in your brand voice. AI-assisted, human-reviewed." },
  { icon: Megaphone, title: "Influencer & Partnership Outreach", desc: "Finding, contacting and managing the right creators for your audience." },
  { icon: Send, title: "Community Management", desc: "Comments, DMs and brand mentions handled professionally, every response human-authored." },
  { icon: Target, title: "Paid Social Advertising", desc: "Meta, Instagram, TikTok and LinkedIn ads. AI tests creative variations faster than any manual process." },
  { icon: BarChart3, title: "Google & Search Ads", desc: "PPC campaigns built, monitored and refined for ROI. AI bid strategies + sharp targeting." },
  { icon: Zap, title: "Email Automation & Flows", desc: "Klaviyo and Marketo flows: welcome, abandoned cart, post-purchase, win-back." },
  { icon: RefreshCcw, title: "Retargeting Campaigns", desc: "Warm conversations brought back with the right messages at the right moment." },
  { icon: FileBarChart, title: "Performance Reporting", desc: "Clear weekly and monthly reports. What ran, what it cost, what it returned, what changes next." },
  { icon: Sparkles, title: "AI Creative Testing", desc: "Generate and test multiple ad variations faster than any traditional process allows." },
];

const TOOLS = [
  { cat: "Email Marketing & Automation", items: ["Mailchimp", "Klaviyo", "Drip", "HubSpot", "ActiveCampaign"] },
  { cat: "Paid Advertising", items: ["Meta Ads Manager", "Google Ads", "TikTok Ads", "LinkedIn Campaign Mgr"] },
  { cat: "SEO & Analytics", items: ["Semrush", "Ahrefs", "Google Search Console", "GA4"] },
  { cat: "Design & Creative", items: ["Canva", "Adobe Express", "Figma", "CapCut", "Midjourney"] },
  { cat: "AI Tools", items: ["ChatGPT", "Copy.ai", "Notion AI", "Jasper", "Grammarly"] },
  { cat: "CRM & Marketing Ops", items: ["HubSpot", "Salesforce", "Zoho", "Pipedrive"] },
  { cat: "Social Media Management", items: ["Buffer", "Hootsuite", "Later", "Sprout Social"] },
];

const TEAM = [
  { initials: "SM", name: "Shreya M.", role: "Marketing VA · 5 yrs", bio: "Ecommerce email marketing & Klaviyo specialist.", tools: ["Klaviyo", "Canva", "Meta Ads", "GA4"] },
  { initials: "RP", name: "Rohan P.", role: "Marketing VA · 6 yrs", bio: "Paid social & creative testing for DTC brands.", tools: ["Meta Ads", "TikTok Ads", "Midjourney", "Canva"] },
  { initials: "AK", name: "Anika K.", role: "Marketing VA · 4 yrs", bio: "SEO + content strategy for SaaS companies.", tools: ["Ahrefs", "Semrush", "Notion", "Jasper"] },
  { initials: "TS", name: "Tanvi S.", role: "Marketing VA · 5 yrs", bio: "Lifecycle email and automation specialist.", tools: ["Klaviyo", "Mailchimp", "HubSpot", "Drip"] },
  { initials: "VK", name: "Varun K.", role: "Marketing VA · 7 yrs", bio: "B2B LinkedIn and demand-gen campaigns.", tools: ["LinkedIn", "HubSpot", "Sales Navigator", "Loom"] },
  { initials: "MJ", name: "Meera J.", role: "Marketing VA · 5 yrs", bio: "Community manager and brand voice keeper.", tools: ["Sprout", "Buffer", "Canva", "ChatGPT"] },
];

const FAQ = [
  { q: "Do your Marketing VAs handle both organic and paid?", a: "Yes — most are trained across paid social, search, organic content and lifecycle email. We match the right specialist to your priority channels." },
  { q: "How does AI actually help my Marketing VA perform better?", a: "AI compresses time on first drafts, creative variations and reporting. Your VA reviews and applies brand judgment so output stays human and on-brand." },
  { q: "Can they work directly in my ad accounts and tools?", a: "Yes. We support agency access, role-based permissions and audit trails. Your US partner monitors all account activity." },
  { q: "How do I know if my Marketing VA is actually performing?", a: "Weekly reports written in plain English: what ran, what it cost, what it returned, what changes next. Plus your US partner reviews results before they reach you." },
  { q: "Can they create content and visuals too?", a: "Yes — copy, light design (Canva, Adobe Express, Figma), short-form video (CapCut), and AI-assisted creative variations." },
  { q: "What if I need someone who specialises in Klaviyo specifically?", a: "We have certified specialists across Klaviyo, HubSpot, Marketo and ActiveCampaign. Tell us your stack — we match the expertise." },
];

export default function MarketingAssistance() {
  return (
    <>
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16">
        <SectionLabel>Marketing Professionals</SectionLabel>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6 items-end">
          <div className="lg:col-span-8" data-reveal>
            <h1 className="font-display font-extrabold tracking-tight text-forest-600 text-5xl md:text-7xl lg:text-[80px] leading-[0.95]">
              Your marketing isn't <br />
              the Problem
              <br />
              <HighlightText><span className="font-serif-italic font-normal">Execution is.</span></HighlightText>{" "}
              <span className="font-serif-italic font-normal">We fix that.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-forest-500 text-sm leading-[1.8]">
              Most businesses don't have a marketing strategy problem. They have an execution problem. Posts that never go out. Email campaigns sitting half-finished. Ad accounts nobody's watching. Our full-time, supervised Marketing VAs handle all of it — content, SEO, paid ads and email flows — with a US-based partner overseeing every outcome. You stop chasing tasks. Your brand keeps moving.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="btn-primary">Get a Marketing VA today <ArrowRight size={16} /></Link>
              <span className="text-xs text-forest-400/80">Bench-ready · Pre-vetted · AI-trained</span>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="aidify-card-dark p-5" data-reveal>
              <div className="text-xs uppercase tracking-[0.18em] text-cream-100/60 mb-4">Output snapshot</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-3xl text-cream-100">10X</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">Output velocity</div></div>
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-3xl text-cream-100">2h</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">Start time</div></div>
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-3xl text-cream-100">12+</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">AI tools</div></div>
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-3xl text-cream-100">US</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">Partner led</div></div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 border-y border-forest-500/10 py-5 overflow-hidden">
          <div className="flex items-center gap-8 whitespace-nowrap">
            <div className="flex gap-10 marquee-track">
              {[...["Social Media", "Paid Ads", "Email Marketing", "SEO", "Content", "LinkedIn Ads", "Google Analytics", "AI Powered Marketing"], ...["Social Media", "Paid Ads", "Email Marketing", "SEO", "Content", "LinkedIn Ads", "Google Analytics", "AI Powered Marketing"]].map((t, i) => (
                <span key={i} className="font-display font-bold text-2xl text-forest-400/40 uppercase tracking-wider">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="The real problem"
          title="You're not short on ideas."
          italic="You're short on people who execute them."
          description="You know what needs to happen. Posts need to go out. Emails need to be written. Ad budgets need watching. But between running your business and managing everything else, marketing keeps getting pushed to tomorrow. And tomorrow never comes."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PROBLEMS.map((p, i) => (
            <div key={i} className={`p-5 ${p.dark ? "aidify-card-dark" : p.sage ? "aidify-card-sage" : "aidify-card"}`} data-reveal>
              <h4 className={`font-display font-bold text-base ${p.dark ? "text-cream-100" : "text-forest-600"}`}>{p.title}</h4>
              <p className={`text-xs mt-2 leading-relaxed ${p.dark ? "text-cream-100/70" : "text-forest-400"}`}>{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THE AIDIFY MODEL */}
      {/* <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="The aidify model"
          title="Not a freelancer."
          italic="A trained specialist who owns the result."
        />
        <div className="aidify-card overflow-hidden">
          {MODEL.map((m, i) => (
            <div key={i} className={`grid grid-cols-12 gap-6 px-6 py-7 items-start border-b border-forest-500/8 last:border-0 ${m.dark ? "bg-forest-600 text-cream-100" : ""}`}>
              <div className="col-span-12 md:col-span-4">
                <h4 className="font-display font-bold text-lg">{m.title}</h4>
                <p className={`mt-2 font-serif-italic text-base ${m.dark ? "text-sage-200" : "text-forest-500"}`}>"{m.quote}"</p>
              </div>
              <div className={`col-span-12 md:col-span-8 text-sm leading-relaxed ${m.dark ? "text-cream-100/75" : "text-forest-400"}`}>{m.desc}</div>
            </div>
          ))}
        </div>
      </section> */}

      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-12">
          <div className="lg:col-span-7" data-reveal>
            <SectionLabel>The aidify model</SectionLabel>
            <h2 className="mt-6 font-display font-extrabold text-forest-600 text-5xl md:text-6xl lg:text-7xl leading-[1.0]">
              Not a freelancer. Not a hire.
            </h2>
            <h3 className="mt-2 font-serif-italic text-forest-600 text-3xl md:text-5xl lg:text-6xl leading-tight">
              <HighlightText>A managed specialist</HighlightText> who owns the result.
            </h3>
          </div>
          <div className="lg:col-span-5 lg:pt-16" data-reveal>
            <p className="text-forest-500 text-[15px] leading-[1.7]">
              We don't send you a freelancer with a fast WiFi connection and hope they show up.
              We don't sell you AI as a shortcut for skill
              Our model is built around full-time Marketing VAs who work in our managed offices, trained on AI tools and human strategy, supervised daily and accountable to a US-based partner who actually knows your brand.
            </p>
          </div>
        </div>

    
        <div className="space-y-4">
          {MODEL.map((m, i) => (
            <div
              key={i}
              data-reveal
              data-i={i + 1}
              className={`p-7 lg:p-9 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 ${m.dark ? "aidify-card-dark" : "aidify-card"}`}
            >
              <div className="lg:col-span-5">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${m.dark ? "bg-forest-500/40" : "bg-sage-200"}`}>
                  <m.icon size={20} className={m.dark ? "text-sage-200" : "text-forest-600"} />
                </div>
                <h4 className={`font-display font-bold text-2xl md:text-3xl leading-tight ${m.dark ? "text-cream-100" : "text-forest-600"}`}>{m.title}</h4>
                <p className={`mt-4 font-serif-italic text-lg md:text-xl leading-snug ${m.dark ? "text-sage-200" : "text-forest-500"}`}>"{m.quote}"</p>
              </div>
              <div className={`lg:col-span-7 text-[15px] leading-[1.75] ${m.dark ? "text-cream-100/80" : "text-forest-400"}`}>
                {m.desc}
              </div>
            </div>
          ))}
        </div>

        
        <div className="mt-16">
          <div className="flex items-center gap-3 mb-8">
            <SectionLabel>AI proficiency, four ways</SectionLabel>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {AI_FOUR_WAYS.map((c, i) => (
              <div key={i} data-reveal data-i={i + 1} className="aidify-card p-6">
                <div className="text-xs font-medium text-forest-400/70 tracking-[0.2em] uppercase">{String(i + 1).padStart(2, "0")}</div>
                <h4 className="mt-3 font-display font-bold text-forest-600 text-lg leading-tight">{c.title}</h4>
                <p className="mt-3 text-sm text-forest-400 leading-[1.7]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT THEY HANDLE */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="What they handle"
          title="Every channel your brand needs to show up on"
          italic="covered, managed and reported."
          description="Our Marketing VAs cover both organic and paid channels. Consistent execution across the board and not a freelancer who's strong on Instagram but has never touched a Google Ads account. AI tools are built into every workflow."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {WHAT_THEY_HANDLE.map((s, i) => (
            <div key={i} className={`aidify-card p-5 ${[2, 7, 9].includes(i) ? "aidify-card-dark" : ""}`} data-reveal>
              <div className={`w-9 h-9 rounded-lg ${[2, 7, 9].includes(i) ? "bg-forest-500/50" : "bg-sage-200"} flex items-center justify-center mb-4`}>
                <s.icon size={16} className={[2, 7, 9].includes(i) ? "text-sage-200" : "text-forest-600"} />
              </div>
              <h4 className={`font-display font-bold text-sm ${[2, 7, 9].includes(i) ? "text-cream-100" : "text-forest-600"}`}>{s.title}</h4>
              <p className={`text-xs mt-2 leading-relaxed ${[2, 7, 9].includes(i) ? "text-cream-100/70" : "text-forest-400"}`}>{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 aidify-card-sage p-5 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <p className="font-display font-bold text-forest-600 text-lg">
            Your competitors are not waiting. <span className="font-serif-italic font-normal">Neither should you.</span>
          </p>
          <Link to="/contact" className="btn-primary text-sm">Talk to Us <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* TOOLS */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Tools they work with"
          title="Trained on the tools"
          italic="your marketing runs on."
          description="Our Marketing VAs don't just know these tools but they use them every day, in production, on live client accounts. Being able to open a tool is not the same as knowing how to get results from it. Our specialists know the difference."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {TOOLS.map((cat, i) => (
            <div key={i} className="aidify-card p-5" data-reveal>
              <div className="text-xs uppercase tracking-[0.18em] text-forest-400 mb-3">{cat.cat}</div>
              <div className="flex flex-wrap gap-1.5">{cat.items.map((t, j) => <ToolPill key={j}>{t}</ToolPill>)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* AI MARKETING TOOLS — dark detail table */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24" data-testid="ai-marketing-tools">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-10">
          <div className="lg:col-span-7" data-reveal>
            <SectionLabel>AI marketing tools</SectionLabel>
            <h2 className="mt-6 font-display font-extrabold text-forest-600 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
              Certified across the stack
            </h2>
            <h3 className="mt-2 font-serif-italic text-forest-600 text-3xl md:text-4xl lg:text-5xl leading-tight">
              that <HighlightText>powers modern</HighlightText> marketing.
            </h3>
          </div>
          <div className="lg:col-span-5 lg:pt-14" data-reveal>
            <p className="text-forest-500 text-[15px] leading-[1.7]">
              Every Marketing VA on our bench is trained on a structured set of AI tools that handle
              content, design, ad testing, analytics and reporting. We measure proficiency before
              they ever touch a client account.
            </p>
          </div>
        </div>

        <div className="aidify-card-dark p-7 md:p-10" data-reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-cream-100/10">
            {[AI_MARKETING_TOOLS.slice(0, 5), AI_MARKETING_TOOLS.slice(5, 10)].map((col, ci) => (
              <ul key={ci} className={`space-y-5 ${ci === 1 ? "md:pl-10 pt-5 md:pt-0" : "md:pr-10"}`}>
                {col.map((t, i) => (
                  <li key={i} className="flex items-start gap-4 group">
                    <div className="shrink-0 w-9 h-9 rounded-lg bg-forest-500/40 group-hover:bg-sage-200 transition-colors flex items-center justify-center mt-0.5">
                      <Sparkles size={14} className="text-sage-200 group-hover:text-forest-600 transition-colors" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-cream-100 text-base md:text-lg">{t.name}</div>
                      <div className="text-cream-100/65 text-sm leading-relaxed mt-0.5">{t.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-cream-100/10">
            <p className="font-serif-italic text-sage-200 text-base md:text-lg leading-relaxed">
              Every Marketing VA on our bench is trained and assessed on these tools before working
              on any client account. Proficiency is not assumed. It is verified.
            </p>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Industries we serve"
          title="We speak your"
          italic="industry's marketing language."
          description="We match every client based on sector experience and not just job title. Your Marketing VA understands how your customers think before work begins."
        />
        <IndustriesGrid featured={INDUSTRIES_MARKETING.featured} others={INDUSTRIES_MARKETING.others} />
      </section>

      {/* COMPARISON */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="The honest comparison"
          title="The math is not even close"
          italic="see for yourself."
          description="The average US marketing coordinator costs $65,000–$85,000 a year before benefits. A freelancer is cheaper but comes with no oversight, no consistency and no one accountable when results don’t show up. Here is how Aidify compares across the things that actually matter."
        />
        <ComparisonTable />

        <div className="aidify-card-dark mt-6 p-7 md:p-10 grid grid-cols-1 md:grid-cols-12 gap-6 items-center" data-reveal>
          <div className="md:col-span-8">
            <h3 className="font-display font-extrabold text-cream-100 text-2xl md:text-3xl leading-tight">
              Still comparing options?
            </h3>
            <p className="mt-2 font-serif-italic text-sage-200 text-xl md:text-2xl">
              One conversation will make this easy.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" className="btn-on-dark inline-flex" data-testid="cmp-cta">
              Talk to Us <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Meet your marketing VAs"
          title="The people behind"
          italic="your next campaign."
          description="Every Aidify assistant is vetted, trained, and employed as part of our team. They work from a professional environment, follow defined processes, and are supported by US managers who help keep quality consistent."
        />
        <TeamCarousel members={TEAM} />
      </section>

      {/* FAQ */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Frequently asked"
          title="Questions worth asking"
          italic="before you delegate."
          description="Did not find what you are looking for? Talk with our team and we will walk you through how Aidify works for your situation."
        />
        <FAQAccordion items={FAQ} />
      </section>

      <FinalCTA />
    </>
  );
}
